import servicesUtils from '../utils/service'

/**
 * 關卡式範本編輯器 — 見 docs/plans/UnitTemplateEditor.web.spec.md
 *
 * 「關卡」對應後端的 `diary_quest.unit`（1 起、連續無洞），一關最多 4 題。
 * 關卡與題目的新增／刪除／重排是**操作當下立即送出**（spec §2），
 * 題目內容則是按「儲存此題」才送出。
 *
 * ⚠ 關卡名稱與說明存在 `diary_homework.template.units[]`，**不在 diary_quest**，
 * 所以由 `saveUnit` 單獨送，跟題目內容是兩支不同的 API。
 *
 * ⚠ 這裡照本 repo 慣例 `catch → log → throw`，**呼叫端必須自己 catch**。
 * 既有元件多半沒接（例如 `QuestEdit.vue` 的 addSampleQuest），API 失敗時老師只看到
 * 按鈕按了沒反應。新元件不沿用這個缺口（spec §9）。
 */
export const templateUnitServices = {
    /**
     * 關卡列表。**巢狀結構**：關卡 → 題目 → 畫格。
     *
     * 每個 unit：`{ unit, name, description, quest_count, quests[],
     *              frame_total, sample_total }`
     * 每個 quest：`{ quest_id, quest_title, quest_description,
     *               frame_count, frames[], sample_total }`
     *
     * `frame_total` / `sample_total` 是給刪除確認用的計數，**前端不要自己算**。
     * 0 題的關卡與 0 畫格的題目都會出現在結果裡。
     * @param {number} diaryHomeworkId
     * @returns {Promise<{max_units:number, units:Array}>}
     */
    async getUnitList(diaryHomeworkId) {
        try {
            const url = `${servicesUtils.resourceUrl('getTemplateUnitList')}`
                .replace(':diaryHomeworkId', diaryHomeworkId)
            return await servicesUtils.get(url)
        } catch (err) {
            console.log(`failed to getUnitList err: ${err}`)
            throw err
        }
    },

    /**
     * 在最後新增一個關卡。超過該地圖的 max_units 時後端回 403。
     *
     * ⚠ **只建關卡，不建題目**（2026-08-28 起），所以**沒有 body、不收 mode**。
     * 新關卡是空的，UnitList 的卡片會標出「還沒有題目」。
     * 模式由關卡內的「新增題目」問（`addQuest`）——它是每一題的屬性而且不可變更
     * （ADR 0002），在按「新增關卡」的時機問等於逼老師在還沒想清楚時做不可逆決定。
     * @param {number} diaryHomeworkId
     * @returns {Promise<{unit:number}>}
     */
    async addUnit(diaryHomeworkId) {
        try {
            const url = `${servicesUtils.resourceUrl('addTemplateUnit')}`
                .replace(':diaryHomeworkId', diaryHomeworkId)
            return await servicesUtils.post(url, {})
        } catch (err) {
            console.log(`failed to addUnit err: ${err}`)
            throw err
        }
    },

    /**
     * 重排關卡。整份送 **unit 編號的新順序**，集合必須與目前完全相同，
     * 否則後端回 400 且資料不變。
     *
     * ⚠ 2026-08-11 契約變更：原本送 `quest_ids`。`[3, 1, 2]` 的意思是
     * 「原本的第 3 關變成第 1 關」。
     * @param {number} diaryHomeworkId
     * @param {number[]} unitList 現有 unit 編號的新順序
     */
    async reorderUnits(diaryHomeworkId, unitList) {
        try {
            const url = `${servicesUtils.resourceUrl('reorderTemplateUnits')}`
                .replace(':diaryHomeworkId', diaryHomeworkId)
            /// 用 POST 不是 PUT/PATCH：全站慣例（見 spec §2 與 SafetyAlert 的 CORS 經驗）
            return await servicesUtils.post(url, { units: unitList })
        } catch (err) {
            console.log(`failed to reorderUnits err: ${err}`)
            throw err
        }
    },

    /**
     * 寫關卡自己的名稱、說明與「自由撰寫」設定（存在 `template.units[]`，不是 diary_quest）。
     *
     * ⚠ 後端**只覆寫這四個 key**，其餘（`chapter` / `blocked` / `label` / `img_url`…）
     * 原樣保留。那些是規格未涵蓋但實際存在的資料，重建物件會清光。
     *
     * @param {number} diaryHomeworkId
     * @param {number} unit
     * @param {string} name 空字串合法（老師還沒命名）
     * @param {string} description
     * @param {boolean} [freeCreation] 自由撰寫的開關。
     *   ⚠ **沒給就是「不動」而不是「關掉」**：這兩個 key 在 22 本既有範本裡有資料，
     *   都是手動改資料庫設的，送 false 會靜默清掉。
     * @param {number|null} [freeCount] 總篇數上限（1–10）。
     *   ⚠⚠ **是總數，含老師出的題目。** 學生端的條件是
     *   `chapter.quests.length >= (count || 3)`，而那個 quests 包含範本題目——
     *   所以一關有 1 道題目、上限 3 時，學生只能再寫 2 篇。UI 要把減法算給老師看。
     *   ⚠ `null` = 不動 `count` 那個 key（關掉開關時送它，數字保留給下次打開）。
     *   ⚠ 上限小於該關現有題目數時後端回 **400**（那個設定會讓學生一篇都寫不了）。
     */
    async saveUnit(diaryHomeworkId, unit, name, description, freeCreation, freeCount) {
        try {
            const url = `${servicesUtils.resourceUrl('saveTemplateUnit')}`
                .replace(':diaryHomeworkId', diaryHomeworkId)
                .replace(':unit', unit)
            const body = { name: name, description: description }
            /// ⚠ 只有明確給了布林才放進 body——`undefined` 不能變成 `false`（見上）。
            if (typeof freeCreation === 'boolean') {
                body.free_creation = freeCreation
            }
            if (typeof freeCount === 'number') {
                body.count = freeCount
            }
            return await servicesUtils.post(url, body)
        } catch (err) {
            console.log(`failed to saveUnit err: ${err}`)
            throw err
        }
    },

    /**
     * 刪除一整個關卡。後端硬刪**該關所有題目**與它們的畫格、範例，
     * 並重編後續關卡的 unit。不可復原。
     *
     * ⚠ 2026-08-11 參數由 questId 改為 unit。兩者都是數字，傳錯不會有錯誤訊息。
     * @param {number} diaryHomeworkId
     * @param {number} unit
     */
    async deleteUnit(diaryHomeworkId, unit) {
        try {
            const url = `${servicesUtils.resourceUrl('deleteTemplateUnit')}`
                .replace(':diaryHomeworkId', diaryHomeworkId)
                .replace(':unit', unit)
            return await servicesUtils.delete(url)
        } catch (err) {
            console.log(`failed to deleteUnit err: ${err}`)
            throw err
        }
    },

    /**
     * 在既有關卡裡加一題（加在該關最後）。超過一關 4 題時後端回 403。
     *
     * ⚠ **`mode` 是必填的**，與 addUnit 同一個理由。而且**這一支不能漏**：
     * 模式是每一題的屬性而一關最多 4 題，只有「新增關卡」帶 mode 的話，
     * 從這裡進來的第 2–4 題會沒有模式（規格 §5.1）。
     * @param {number} diaryHomeworkId
     * @param {number} unit
     * @param {string} mode 七種之一，見 addUnit
     * @param {string} [frameTitle] **只有找單字用得到**：它建立時會自動補一格
     *   （學生玩完會自動存成一篇紀錄），這是那一格的**預設標題**。
     *   ⚠ 由前端給是刻意的——後端在這個時間點拿不到語系（`diary_quest.locale` 要到第一次
     *   存題目才寫得進去，`diary_homework` 沒有語系欄位），寫死一個字串會讓日文老師
     *   拿到中文的預設標題。文案在 `unit.frame.wordsearch_title_default`。
     *   ⚠ 其他模式**不要送**：後端會忽略，但送了會讓人以為每個模式都有這一格。
     * @returns {Promise<{quest_id:number, unit:number, display_order:number}>}
     */
    async addQuest(diaryHomeworkId, unit, mode, frameTitle) {
        try {
            const url = `${servicesUtils.resourceUrl('addTemplateQuest')}`
                .replace(':diaryHomeworkId', diaryHomeworkId)
                .replace(':unit', unit)
            return await servicesUtils.post(url, servicesUtils.omitNullish({
                mode: mode,
                frame_title: frameTitle,
            }))
        } catch (err) {
            console.log(`failed to addQuest err: ${err}`)
            throw err
        }
    },

    /**
     * 重排一個關卡內的題目。整份送該關的 quest_id 新順序。
     * @param {number} diaryHomeworkId
     * @param {number} unit
     * @param {number[]} questIdList
     */
    async reorderQuests(diaryHomeworkId, unit, questIdList) {
        try {
            const url = `${servicesUtils.resourceUrl('reorderTemplateQuests')}`
                .replace(':diaryHomeworkId', diaryHomeworkId)
                .replace(':unit', unit)
            return await servicesUtils.post(url, { quest_ids: questIdList })
        } catch (err) {
            console.log(`failed to reorderQuests err: ${err}`)
            throw err
        }
    },

    /**
     * 刪除單獨一題（關卡留著）。**允許刪到 0 題**。
     * @param {number} diaryHomeworkId
     * @param {number} questId
     */
    async deleteQuest(diaryHomeworkId, questId) {
        try {
            const url = `${servicesUtils.resourceUrl('deleteTemplateQuest')}`
                .replace(':diaryHomeworkId', diaryHomeworkId)
                .replace(':questId', questId)
            return await servicesUtils.delete(url)
        } catch (err) {
            console.log(`failed to deleteQuest err: ${err}`)
            throw err
        }
    },

    /**
     * 上傳題目封面圖。後端會壓縮（長邊 1000、JPEG q82、有透明才輸出 PNG）後放上 S3，
     * 並把網址寫進 `diary_quest.resources.thumbnail_url`。
     *
     * 換圖時後端會刪掉**它自己上傳過的**舊檔；既有的外部網址不會被動到。
     * @param {number} diaryHomeworkId
     * @param {number} questId
     * @param {File} file
     * @returns {Promise<{thumbnail_url:string}>}
     */
    async uploadQuestThumbnail(diaryHomeworkId, questId, file) {
        try {
            const url = `${servicesUtils.resourceUrl('questThumbnail')}`
                .replace(':diaryHomeworkId', diaryHomeworkId)
                .replace(':questId', questId)
            return await servicesUtils.postFormData(url, { file: file })
        } catch (err) {
            console.log(`failed to uploadQuestThumbnail err: ${err}`)
            throw err
        }
    },

    /**
     * 移除題目封面圖。縮圖是選填的，移除後學生端會顯示它自己的預設圖。
     * @param {number} diaryHomeworkId
     * @param {number} questId
     */
    async deleteQuestThumbnail(diaryHomeworkId, questId) {
        try {
            const url = `${servicesUtils.resourceUrl('questThumbnail')}`
                .replace(':diaryHomeworkId', diaryHomeworkId)
                .replace(':questId', questId)
            return await servicesUtils.delete(url)
        } catch (err) {
            console.log(`failed to deleteQuestThumbnail err: ${err}`)
            throw err
        }
    },

    /**
     * 學生地圖預覽：底圖網址、底圖長寬，以及**依目前關卡數**的節點座標。
     *
     * ⚠ `map.units` 是每一種關卡總數各有一組完全不同的座標，不是一組取前 N 個。
     * 所以關卡增刪或重排之後要**重新呼叫這支**，不能只補一顆節點。
     * 找不到對應關卡數時 `nodes` 是空陣列。
     * @param {number} diaryHomeworkId
     * @returns {Promise<{background_url:string, width:number, height:number, nodes:Array}>}
     */
    async getMapPreview(diaryHomeworkId) {
        try {
            const url = `${servicesUtils.resourceUrl('getTemplateMapPreview')}`
                .replace(':diaryHomeworkId', diaryHomeworkId)
            return await servicesUtils.get(url)
        } catch (err) {
            console.log(`failed to getMapPreview err: ${err}`)
            throw err
        }
    },

    /**
     * 建立關卡式範本時可選的地圖清單。
     * @returns {Promise<{maps:Array<{map_id:number, background_url:string, max_units:number}>}>}
     *   ⚠ `background_url` 可能是相對路徑（實測 map 3），不可假設為絕對 URL
     */
    async getAvailableMaps() {
        try {
            const url = `${servicesUtils.resourceUrl('getAvailableMaps')}`
            return await servicesUtils.get(url)
        } catch (err) {
            console.log(`failed to getAvailableMaps err: ${err}`)
            throw err
        }
    },

    /**
     * 用 `tp_` 分享代碼領取一份範本的深拷貝（studio-api `docs/plans/TemplateShare.spec.md`）。
     *
     * 傳 `{ handleError: true }`：錯誤要在對話框的輸入框下方顯示，不能被換到 `/404`。
     * 失敗時 throw，`err.status`：400 不是 tp_ 代碼、404 找不到或已停用、409 來源範本含子地圖。
     * @param {string} shareCode
     * @returns {Promise<{data:{diary_homework_id:number}}>}
     */
    async claimTemplateShareCode(shareCode) {
        try {
            const url = `${servicesUtils.resourceUrl('claimTemplateShareCode')}`
            return await servicesUtils.post(url, { share_code: shareCode }, { handleError: true })
        } catch (err) {
            console.log(`failed to claimTemplateShareCode err: ${err}`)
            throw err
        }
    },

    /**
     * 範本目前的分享代碼，`share_code` 可能是 null（沒有分享或已停用）。
     * 不是擁有者、或不是自己的關卡式範本（例如領來的副本）時 throw，`err.status === 404`：
     * 編輯頁靠這個決定要不要顯示分享區塊。
     * @param {number} diaryHomeworkId
     * @returns {Promise<{data:{share_code:string|null}}>}
     */
    async getShareCode(diaryHomeworkId) {
        try {
            const url = `${servicesUtils.resourceUrl('templateShareCode')}`
                .replace(':diaryHomeworkId', diaryHomeworkId)
            return await servicesUtils.get(url, { handleError: true })
        } catch (err) {
            console.log(`failed to getShareCode err: ${err}`)
            throw err
        }
    },

    /**
     * 產生或換一組分享代碼，舊的立即失效。有子地圖或留言板時 `err.status === 409`。
     * @param {number} diaryHomeworkId
     * @returns {Promise<{data:{share_code:string}}>}
     */
    async regenerateShareCode(diaryHomeworkId) {
        try {
            const url = `${servicesUtils.resourceUrl('templateShareCode')}`
                .replace(':diaryHomeworkId', diaryHomeworkId)
            return await servicesUtils.post(url, {}, { handleError: true })
        } catch (err) {
            console.log(`failed to regenerateShareCode err: ${err}`)
            throw err
        }
    },

    /**
     * 停用分享代碼。已經領走的副本不受影響。
     * @param {number} diaryHomeworkId
     */
    async disableShareCode(diaryHomeworkId) {
        try {
            const url = `${servicesUtils.resourceUrl('templateShareCode')}`
                .replace(':diaryHomeworkId', diaryHomeworkId)
            return await servicesUtils.delete(url, undefined, { handleError: true })
        } catch (err) {
            console.log(`failed to disableShareCode err: ${err}`)
            throw err
        }
    },

    /**
     * 這一題互動影片問答的語音狀態（存檔後背景產生），編輯器在有句子「產生中」時定時查。
     * 傳 `{ handleError: true }`：查詢失敗只是這一輪沒更新，不能把老師換到錯誤頁。
     * @param {number} diaryHomeworkId
     * @param {number} questId
     * @returns {Promise<{data:{media:Object<string, {media_id:number, status:string,
     *   process_status:string, url:string|null, duration_ms:number|null}>}}>}
     */
    async getQuestAudioStatus(diaryHomeworkId, questId) {
        try {
            const url = `${servicesUtils.resourceUrl('questAudioStatus')}`
                .replace(':diaryHomeworkId', diaryHomeworkId)
                .replace(':questId', questId)
            return await servicesUtils.get(url, { handleError: true })
        } catch (err) {
            console.log(`failed to getQuestAudioStatus err: ${err}`)
            throw err
        }
    },
}
